import React, { useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Pages
import Home from './pages/Home';
import SalonListing from './pages/SalonListing';
import SalonDetail from './pages/SalonDetail';
import BookingConfirmation from './pages/BookingConfirmation';
import Login from './pages/Login';
import Register from './pages/Register';
import About from './pages/About';
import Contact from './pages/Contact';
import CustomerDashboard from './pages/CustomerDashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AuthCallback from './pages/AuthCallback';
import CreateSalon from './pages/CreateSalon';

import './App.css';

// Protected Route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { state: { from: location } });
    } else if (!loading && isAuthenticated && allowedRoles && !allowedRoles.includes(user?.role)) {
      navigate('/');
    }
  }, [loading, isAuthenticated, user, allowedRoles, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return null;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/salons" element={<SalonListing />} />
      <Route path="/salons/:category" element={<SalonListing />} />
      <Route path="/salon/:salonId" element={<SalonDetail />} />
      <Route path="/booking-confirmation/:bookingId" element={<BookingConfirmation />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Customer Dashboard */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['customer']}>
          <CustomerDashboard />
        </ProtectedRoute>
      } />

      {/* Shop Owner Dashboard */}
      <Route path="/owner/create-salon" element={
  <ProtectedRoute allowedRoles={['shop_owner']}>
    <CreateSalon />
  </ProtectedRoute>
} />
      <Route path="/owner" element={
        <ProtectedRoute allowedRoles={['shop_owner']}>
          <OwnerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/owner/*" element={
        <ProtectedRoute allowedRoles={['shop_owner']}>
          <OwnerDashboard />
        </ProtectedRoute>
      } />

      {/* Admin Dashboard */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
